import { Icon } from "@circle-react-shared/Icon";
import { Button as CompassButton } from "@circleco/compass/components/Button";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { invalidateAllMemberQueries } from "@/react/components/ProfileModalV3/Body/ConnectBanner/util";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useWithdrawConnectionConfirmModal } from "@/react/components/shared/MembersList/CommunityMembers/ActionButtons/WithdrawConnectionConfirmModal";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useWithdrawConnection } from "@/react/hooks/connections/useWithdrawConnection";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import type { PendingButtonProps } from "./types";

const sizeClasses = {
  sm: "!h-7 !px-3 gap-1.5",
  md: "!h-8 !px-4 gap-2",
  lg: "!h-10 !px-5 gap-2.5",
  full: "w-full",
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
  full: 16,
};

export const PendingButton = ({
  connectionId,
  recipientName,
  memberPublicUid,
  size = "md",
  className,
  disabled,
  fullWidth = false,
}: PendingButtonProps) => {
  const { isCircleV4 } = useCircleV4();
  const withdrawConnectionConfirmModal = useWithdrawConnectionConfirmModal();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: withdrawConnection, isPending: isWithdrawing } =
    useWithdrawConnection({
      onSuccess: async () => {
        await invalidateAllMemberQueries(queryClient, memberPublicUid ?? "");
        toast.success(
          t("profile_drawer.connect_banner.messages.connection_withdrawn"),
        );
      },
    });

  const handleWithdrawConnection = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    await withdrawConnectionConfirmModal.show({
      onConfirm: () => {
        void withdrawConnectionConfirmModal.hide();
        if (connectionId) {
          withdrawConnection(connectionId);
        }
      },
      onCancel: () => {
        void withdrawConnectionConfirmModal.hide();
      },
      disabled: isWithdrawing,
      recipient: recipientName,
    });
  };

  if (isCircleV4) {
    return (
      <CompassButton
        variant="outline"
        startIcon="clock-dash"
        loading={isWithdrawing}
        disabled={disabled}
        onClick={handleWithdrawConnection}
        aria-label={t("pending")}
        data-testid="pending-button"
        fullWidth={fullWidth}
        className={className}
      >
        {t("pending")}
      </CompassButton>
    );
  }

  const iconSize = iconSizes[size];

  return (
    <Button
      variant="secondary"
      className={classNames(
        "bg-secondary hover:!bg-tertiary hover:!border-secondary !border-transparent !font-medium flex justify-center items-center !gap-1 min-w-30",
        sizeClasses[size],
        className,
      )}
      onClick={handleWithdrawConnection}
      disabled={disabled || isWithdrawing}
      aria-label={t("pending")}
      data-testid="pending-button"
      large={size === "lg"}
    >
      {isWithdrawing ? (
        <Icon type="loader" />
      ) : (
        <>
          <Icon
            type="16-clock"
            size={iconSize}
            useWithFillCurrentColor
            className="mr-1"
          />
          <Typography.LabelSm color="text-primary" weight="font-medium">
            {t("pending")}
          </Typography.LabelSm>
        </>
      )}
    </Button>
  );
};
